/**
 * Local Save Utility
 * 
 * Uses the File System Access API to save generated files locally
 * with an organized folder structure similar to Firebase Storage.
 * 
 * Folder Structure, as the library has the workspace (utils/localPlaces.ts):
 * {UserSelectedFolder}/
 * └── {Account}/                  (named after the signed-in account, so accounts that share the folder stay apart)
 *     └── {Project}/{Folder…}/    (none for a workspace in no project; Shared_with_me for one shared with you)
 *         └── {WorkspaceName}/
 *             ├── Generated_Images/
 *             │   └── image_1734567890.png
 *             ├── Videos/
 *             │   └── video_1734567890.mp4
 *             └── Sketches/
 *                 └── sketch_1734567890.png
 *
 * Files saved before accounts had folders of their own sit at the top, under their project or
 * under their workspace's name; they are looked for there too, and the desktop app moves them.
 */

import { saveLocalStoragePrefs } from './storage';
import { folderSegment, placeKey, planFolderMoves, type FolderMove, type WorkspacePlace } from './localPlaces';
import { mediaFileName } from './mediaNames';
import {
    DesktopDirectoryHandle, DesktopFileHandle, desktopApp, desktopFolder, desktopMediaUrl, folderAccount, forgetDesktopFolder, isLocalMediaUrl,
    pickDesktopFolder, withoutLocalMediaSecrets, type DesktopLocated, type NoodleDesktop,
} from './desktopFolder';

// IndexedDB for persisting directory handle
const DB_NAME = 'LocalSaveDB';
const STORE_NAME = 'directoryHandles';
const HANDLE_KEY = 'saveDirectory';

// --- USER-SCOPED LOCALSTORAGE HELPERS ---
// All localStorage keys are prefixed with userId to prevent cross-user leakage
const userKey = (userId: string | undefined, key: string): string => {
    if (!userId) return key; // Fallback for unauthenticated access
    return `${userId}:${key}`;
};

/**
 * Get a user-scoped localStorage value.
 * Falls back to legacy (un-prefixed) key if user-scoped key is absent,
 * and migrates it on read.
 */
export const getUserLocalStorage = (userId: string | undefined, key: string): string | null => {
    if (userId) {
        const scoped = localStorage.getItem(userKey(userId, key));
        if (scoped !== null) return scoped;
        // Migrate legacy un-prefixed value
        const legacy = localStorage.getItem(key);
        if (legacy !== null) {
            localStorage.setItem(userKey(userId, key), legacy);
            // Don't delete legacy key here — another user may still need migration
            return legacy;
        }
        return null;
    }
    return localStorage.getItem(key);
};

/**
 * Set a user-scoped localStorage value.
 */
export const setUserLocalStorage = (userId: string | undefined, key: string, value: string): void => {
    localStorage.setItem(userKey(userId, key), value);
};

/**
 * Remove a user-scoped localStorage value.
 */
export const removeUserLocalStorage = (userId: string | undefined, key: string): void => {
    localStorage.removeItem(userKey(userId, key));
};

/** The folders a workspace's files are saved under; a copy of a cloud file goes under the cloud copy's own category, whatever it is */
export type SaveCategory = 'Generated_Images' | 'Uploaded_Images' | 'Videos' | 'Audio' | 'Sketches' | 'Uploads';

// Global map to track file:// reference to local path mappings
// This is populated when files are saved locally
let localFileMapCache: Record<string, string> = {};

/**
 * Register a file mapping (file:// reference -> local path)
 * Called when a file is saved locally alongside its cloud upload
 */
export const registerLocalFileMapping = (fileRef: string, localPath: string): void => {
    localFileMapCache[fileRef] = localPath;
    console.log(`📁 Registered file mapping: ${fileRef} -> ${localPath}`);
};

/**
 * Get all current file mappings
 */
export const getLocalFileMap = (): Record<string, string> => {
    return { ...localFileMapCache };
};

/** Where one reference was saved locally (Category/filename), without copying the whole map. */
export const localPathForRef = (fileRef: string): string | undefined => localFileMapCache[fileRef];

/**
 * Clear file mappings (e.g., when switching workspaces)
 */
export const clearLocalFileMap = (): void => {
    localFileMapCache = {};
};

/**
 * Set file mappings (e.g., when loading from workspace)
 */
export const setLocalFileMap = (map: Record<string, string>): void => {
    localFileMapCache = { ...map };
    console.log(`📁 Loaded ${Object.keys(map).length} file mappings`);
};

interface LocalSaveSettings {
    enabled: boolean;
    directoryHandle: FileSystemDirectoryHandle | null;
    lastUsedPath: string | null;
}

// Check if File System Access API is supported (the desktop app keeps the folder itself)
export const isFileSystemAccessSupported = (): boolean => {
    return !!desktopApp() || 'showDirectoryPicker' in window;
};

// Open IndexedDB
const openDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        
        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
    });
};

/**
 * Handles already read from IndexedDB, by storage key. Every local media read asks for
 * the handle, so it is read once per page load; picking or clearing a folder resets it,
 * in this tab directly and in the app's other tabs through folderChannel.
 */
const handleCache = new Map<string, Promise<FileSystemDirectoryHandle | null>>();

/** A file of the local folder, ready to be shown. */
export interface LocalMedia {
    /** The address elements load it from. It lives for this session only: never save it. */
    url: string;
    size: number;
    lastModified: number;
    /** True when the address streams the file from disk (the desktop app); false for an object URL of the file. */
    streamed: boolean;
}

/**
 * Files already found in this session (locateLocalFile), by account, workspace and name, so
 * every place that shows a file shares one lookup and one address, and a workspace opened
 * again looks nothing up. Only finds are kept: a file that was missing is looked for again
 * the next time. Everything is forgotten when the folder or access to it changes; one file
 * when the app writes, moves or removes it.
 */
const locatedFiles = new Map<string, Promise<LocalMedia | null>>();
const foundFiles = new Map<string, LocalMedia>();
/** The addresses of the finds above, with how many finds share each: an address not here is no longer good. */
const currentUrls = new Map<string, number>();
const locatedKey = (account: string, workspace: string, category: string, filename: string, storageWorkspace?: string): string =>
    [account, workspace, category, filename, storageWorkspace || ''].join('\n');

/**
 * Object URLs made for found files (a browser's folder; the desktop app streams instead), with
 * how many views show each. A browser backs them with the file on disk, so one costs next to
 * nothing, but a long session must not gather them without end: past OBJECT_URL_LIMIT the
 * least recently used ones are released, and so is any whose find was forgotten. Where the
 * file's bytes are held in memory instead (the desktop app without its built-in server), they
 * are released sooner, past OBJECT_URL_MEMORY_LIMIT in all. One that a view still shows is
 * never released, nor one used within the last minute (a view takes hold of an address a
 * moment after it was handed out).
 */
interface ObjectUrlUse { key: string | null; holders: number; usedAt: number; /** Bytes held in memory for it; 0 when the browser reads the file from disk. */ memory: number }
const objectUrls = new Map<string, ObjectUrlUse>();
const OBJECT_URL_LIMIT = 300;
const OBJECT_URL_MEMORY_LIMIT = 512 * 1024 * 1024;
const OBJECT_URL_GRACE_MS = 60_000;

const releaseUnusedObjectUrls = (): void => {
    const now = Date.now();
    let memory = 0;
    for (const use of objectUrls.values()) memory += use.memory;
    // The map is kept in order of use, so the first entries are the least recently used
    for (const [url, use] of objectUrls) {
        if (use.holders > 0 || now - use.usedAt <= OBJECT_URL_GRACE_MS) continue;
        const tooMany = objectUrls.size > OBJECT_URL_LIMIT || (use.memory > 0 && memory > OBJECT_URL_MEMORY_LIMIT);
        if (use.key !== null && !tooMany) continue;
        if (use.key !== null) dropLocated(use.key);
        objectUrls.delete(url);
        memory -= use.memory;
        try { URL.revokeObjectURL(url); } catch { /* already gone */ }
    }
};

const usedObjectUrl = (url: string): void => {
    const use = objectUrls.get(url);
    if (!use) return;
    use.usedAt = Date.now();
    objectUrls.delete(url);
    objectUrls.set(url, use);
};

const dropLocated = (key: string): void => {
    locatedFiles.delete(key);
    const media = foundFiles.get(key);
    if (!media) return;
    foundFiles.delete(key);
    const left = (currentUrls.get(media.url) || 1) - 1;
    if (left > 0) currentUrls.set(media.url, left); else currentUrls.delete(media.url);
    const use = objectUrls.get(media.url);
    if (use) use.key = null;
};

/**
 * Which file reference a local address stands for. The module that resolves references
 * (utils/displayUrl.ts) knows both and says so here; this module cannot ask it directly.
 */
let referenceOfLocalUrl: (url: string) => string | undefined = () => undefined;
export const setLocalUrlReferences = (lookup: (url: string) => string | undefined): void => {
    referenceOfLocalUrl = lookup;
};

/** How often the folder's contents changed through the app in this session; see localFolderRevision. */
let folderRevision = 0;

/** Forget what was found: everything, or one file (by category and name, in whichever workspace folder). */
const forgetLocated = (category?: string, filename?: string): void => {
    for (const key of Array.from(locatedFiles.keys())) {
        if (category !== undefined) {
            const parts = key.split('\n');
            if (parts[2] !== category || parts[3] !== filename) continue;
        }
        dropLocated(key);
    }
    folderRevision++;
    releaseUnusedObjectUrls();
};

/** Fired on window when the local folder becomes readable or changes, so views can switch to local copies. */
export const LOCAL_FOLDER_ACCESS_EVENT = 'noodle:local-folder-access';

const folderChannel: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('noodle-local-folder') : null;
// Node (tests) would otherwise keep running for the channel; browsers have no unref.
(folderChannel as unknown as { unref?: () => void } | null)?.unref?.();

/** Tell this tab's views, and the app's other tabs, that the folder or access to it changed. */
const announceFolderChange = (toOtherTabs: boolean): void => {
    folderIndex = null;
    forgetLocated();
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(LOCAL_FOLDER_ACCESS_EVENT));
    if (toOtherTabs) {
        try { folderChannel?.postMessage('changed'); } catch { /* channel closed */ }
    }
};

folderChannel?.addEventListener('message', () => {
    handleCache.clear();
    announceFolderChange(false);
});

// Store directory handle in IndexedDB (scoped to userId)
export const storeDirectoryHandle = async (handle: FileSystemDirectoryHandle, userId?: string): Promise<void> => {
    handleCache.clear();
    // The desktop app remembered the folder when it was chosen
    if (desktopApp()) return;
    const db = await openDB();
    const key = userId ? `${HANDLE_KEY}_${userId}` : HANDLE_KEY;
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const request = store.put(handle, key);
        
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

// Retrieve directory handle from IndexedDB (scoped to userId)
export const getStoredDirectoryHandle = (userId?: string): Promise<FileSystemDirectoryHandle | null> => {
    const key = userId ? `${HANDLE_KEY}_${userId}` : HANDLE_KEY;
    let pending = handleCache.get(key);
    if (!pending) {
        pending = readStoredDirectoryHandle(userId);
        handleCache.set(key, pending);
        // A miss or a failed read is not remembered, so the next call looks again
        pending.then(handle => { if (!handle) handleCache.delete(key); }, () => handleCache.delete(key));
    }
    return pending;
};

const readStoredDirectoryHandle = async (userId?: string): Promise<FileSystemDirectoryHandle | null> => {
    const desktop = desktopApp();
    if (desktop) {
        try { return await desktopFolder(desktop, folderAccount(userId)); } catch { return null; }
    }
    try {
        const db = await openDB();
        const key = userId ? `${HANDLE_KEY}_${userId}` : HANDLE_KEY;
        return new Promise(async (resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const request = store.get(key);
            
            request.onsuccess = async () => {
                if (request.result) {
                    resolve(request.result);
                } else if (userId) {
                    // Migrate legacy un-scoped handle to user-scoped key
                    try {
                        const legacyDb = await openDB();
                        const legacyTx = legacyDb.transaction(STORE_NAME, 'readonly');
                        const legacyStore = legacyTx.objectStore(STORE_NAME);
                        const legacyReq = legacyStore.get(HANDLE_KEY);
                        legacyReq.onsuccess = async () => {
                            if (legacyReq.result) {
                                // Copy legacy handle to user-scoped key
                                await storeDirectoryHandle(legacyReq.result, userId);
                                console.log(`📁 Migrated legacy directory handle to user: ${userId}`);
                                resolve(legacyReq.result);
                            } else {
                                resolve(null);
                            }
                        };
                        legacyReq.onerror = () => resolve(null);
                    } catch {
                        resolve(null);
                    }
                } else {
                    resolve(null);
                }
            };
            request.onerror = () => reject(request.error);
        });
    } catch {
        return null;
    }
};

// Clear stored directory handle (scoped to userId)
export const clearStoredDirectoryHandle = async (userId?: string): Promise<void> => {
    handleCache.clear();
    announceFolderChange(true);
    const desktop = desktopApp();
    if (desktop) {
        await forgetDesktopFolder(desktop, folderAccount(userId)).catch(() => {});
        return;
    }
    try {
        const db = await openDB();
        const key = userId ? `${HANDLE_KEY}_${userId}` : HANDLE_KEY;
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.delete(key);
            
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    } catch {
        // Ignore errors
    }
};

/**
 * Whether the app may use the stored folder. When it may not yet, and `request` is true,
 * the browser is asked, which only works during a user action such as a click. Reads
 * that run in the background pass `request: false`: they must never prompt, and the
 * request would fail without a click anyway.
 */
export const verifyPermission = async (
    handle: FileSystemDirectoryHandle,
    readWrite: boolean = true,
    request: boolean = true
): Promise<boolean> => {
    const options: any = { mode: readWrite ? 'readwrite' : 'read' };
    
    // Check if permission is already granted
    try {
        if ((await (handle as any).queryPermission(options)) === 'granted') {
            return true;
        }
    } catch (e) {
        console.warn('queryPermission failed:', e);
        return false;
    }
    if (!request) return false;

    // Request permission - this requires user activation (e.g., button click)
    // Don't call this automatically on page load or in useEffect
    try {
        if ((await (handle as any).requestPermission(options)) === 'granted') {
            // Access was just given (from Settings, a save, a workflow button): open views can use local copies now
            announceFolderChange(true);
            return true;
        }
    } catch (e: any) {
        // SecurityError: User activation is required
        if (e.name === 'SecurityError') {
            console.warn('requestPermission requires user activation (e.g., button click)');
            return false;
        }
        console.error('requestPermission failed:', e);
        return false;
    }
    
    return false;
};

// Let user pick a directory (scoped to userId)
export const pickSaveDirectory = async (userId?: string): Promise<FileSystemDirectoryHandle | null> => {
    const desktop = desktopApp();
    if (desktop) {
        // Windows' own folder dialog; the desktop app remembers the choice for good
        try {
            const handle = await pickDesktopFolder(desktop, folderAccount(userId));
            if (handle) {
                handleCache.clear();
                announceFolderChange(true);
            }
            return handle;
        } catch (e) {
            console.error('Error picking directory:', e);
            return null;
        }
    }
    if (!isFileSystemAccessSupported()) {
        console.warn('File System Access API not supported');
        return null;
    }
    
    try {
        const handle = await (window as any).showDirectoryPicker({
            mode: 'readwrite',
            startIn: 'downloads',
        });
        
        // Store for future sessions (scoped to userId)
        await storeDirectoryHandle(handle, userId);
        announceFolderChange(true);

        return handle;
    } catch (e: any) {
        if (e.name === 'AbortError') {
            // User cancelled
            return null;
        }
        console.error('Error picking directory:', e);
        return null;
    }
};

/**
 * Sync local storage preferences to Firebase
 * Call this when enabling/disabling local storage
 */
export const syncLocalStoragePrefsToCloud = async (
    userId: string | undefined,
    enabled: boolean,
    folderName: string | null
): Promise<void> => {
    if (!userId) return;
    
    await saveLocalStoragePrefs(userId, {
        hasLocalStorageEnabled: enabled,
        localStoragePath: folderName,
        lastEnabledAt: enabled ? Date.now() : null
    });
    
    console.log(`📤 Synced local storage prefs to cloud: enabled=${enabled}, path=${folderName}`);
};

// Get or create a subdirectory
const getOrCreateDirectory = async (
    parent: FileSystemDirectoryHandle,
    name: string
): Promise<FileSystemDirectoryHandle> => {
    return await parent.getDirectoryHandle(name, { create: true });
};

// Sanitize name for file system
const sanitizeName = folderSegment;

// ── Where workspaces' folders are ────────────────────────────────────────────

/**
 * Each workspace's folder sits where the library has the workspace: Project / Folder… /
 * Workspace (utils/localPlaces.ts). The app says where (setWorkspacePlaces); everything here
 * that is handed a workspace's name finds its folder through placeFor. The open workspace's
 * place wins its name; another's is used while no other workspace has its name. A workspace
 * not known here keeps a folder at the top named after it, as every folder was before.
 */
let placesByName = new Map<string, WorkspacePlace | null>();
let openWorkspacePlace: WorkspacePlace | null = null;
let placesSignature = '';

/** The name of the folder an account's files go under is kept per account (see setLocalFolderAccount) */
const ACCOUNT_KEY = 'localFolderAccount';
/** The signed-in account and the folder its files go under; null when signed out, or not told yet */
let currentAccount: { id: string; dir: string[] } | null = null;

/**
 * Tells which account is signed in and the folder its files go under inside the local folder
 * (utils/localPlaces.ts accountFolderName), so two accounts that chose the same folder keep
 * their files apart. The name an account's files first went under on this device stays its
 * name, whatever the account is called later; it is returned. Null, and no folder of its own,
 * when signed out. Views look again when the account changes: files are now looked for under
 * its folder.
 */
export const setLocalFolderAccount = (userId: string | undefined, folderName: string | null | undefined): string | null => {
    let name: string | null = null;
    if (userId) {
        const kept = getUserLocalStorage(userId, ACCOUNT_KEY);
        name = kept || (folderName ? sanitizeName(folderName) : null);
        if (name && kept !== name) setUserLocalStorage(userId, ACCOUNT_KEY, name);
    }
    const next = userId && name ? { id: userId, dir: [name] } : null;
    if (next?.id !== currentAccount?.id || placeKey(next?.dir ?? []) !== placeKey(currentAccount?.dir ?? [])) {
        currentAccount = next;
        announceFolderChange(false);
    }
    return name;
};

/**
 * The folder an account's files go under, as the chain of folders from the top: the signed-in
 * account's, or the one kept for `userId` on this device. None when signed out or not told yet,
 * as before accounts had folders of their own.
 */
const accountDir = (userId?: string): string[] => {
    if (currentAccount && (!userId || currentAccount.id === userId)) return currentAccount.dir;
    if (!userId) return [];
    const kept = getUserLocalStorage(userId, ACCOUNT_KEY);
    return kept ? [kept] : [];
};

/**
 * What the other accounts that used this device keep in the local folder, from their own
 * settings here: the folders named after them, and the folders they put their workspaces in
 * (where the app last put each, and the last few it wrote to). Those are not this account's to
 * move, or to bring workspaces in from.
 */
const otherAccounts = (userId?: string): { folders: Set<string>; places: Set<string> } => {
    const folders = new Set<string>();
    const places = new Set<string>();
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i) || '';
        const at = key.lastIndexOf(':');
        if (at <= 0) continue;
        const account = key.slice(0, at);
        const name = key.slice(at + 1);
        if (account === userId) continue;
        if (name === ACCOUNT_KEY) {
            const folder = localStorage.getItem(key);
            if (folder) folders.add(folder);
        } else if (name === 'localFolderPlaces' || name === 'localFolderPast') {
            try {
                const value = JSON.parse(localStorage.getItem(key) || '{}') as Record<string, string | string[]>;
                for (const entry of Object.values(value)) for (const dir of Array.isArray(entry) ? entry : [entry]) if (typeof dir === 'string') places.add(dir);
            } catch { /* not the app's */ }
        }
    }
    return { folders, places };
};

/** Where the app last put each workspace's folder (by id), per account: see organiseLocalFolder */
const rememberedPlaces = (userId?: string): Record<string, string> => {
    try {
        const value = JSON.parse(getUserLocalStorage(userId, 'localFolderPlaces') || '{}');
        return value && typeof value === 'object' ? value : {};
    } catch {
        return {};
    }
};
const rememberPlaces = (userId: string | undefined, places: Record<string, string>): void => {
    setUserLocalStorage(userId, 'localFolderPlaces', JSON.stringify(places));
};

/**
 * The last few folders the app wrote each workspace's files to (by id), per account. Where folders
 * are not moved (a browser), a workspace renamed or moved in the library leaves its files there:
 * they are looked for there too.
 */
const pastPlaces = (userId?: string): Record<string, string[]> => {
    try {
        const value = JSON.parse(getUserLocalStorage(userId, 'localFolderPast') || '{}');
        return value && typeof value === 'object' ? value : {};
    } catch {
        return {};
    }
};
const notePlaceUsed = (workspaceName: string, userId?: string): void => {
    const place = placeFor(workspaceName);
    if (!place) return;
    const past = pastPlaces(userId);
    const key = placeKey([...accountDir(userId), ...place.dir]);
    if (past[place.id]?.[0] === key) return;
    past[place.id] = [key, ...(past[place.id] ?? []).filter(dir => dir !== key)].slice(0, 5);
    setUserLocalStorage(userId, 'localFolderPast', JSON.stringify(past));
};

/** Tells where the library has each workspace, and which one is open. Lookups made before are forgotten when anything moved. */
export const setWorkspacePlaces = (places: WorkspacePlace[], openId?: string | null): void => {
    const byName = new Map<string, WorkspacePlace | null>();
    for (const place of places) byName.set(place.name, byName.has(place.name) ? null : place);
    const open = openId ? places.find(place => place.id === openId) ?? null : null;
    const signature = JSON.stringify([places.map(place => [place.id, place.name, placeKey(place.dir)]), open?.id ?? null]);
    placesByName = byName;
    openWorkspacePlace = open;
    if (signature !== placesSignature) {
        placesSignature = signature;
        forgetLocated();
    }
};

const placeFor = (workspaceName: string): WorkspacePlace | null =>
    openWorkspacePlace?.name === workspaceName ? openWorkspacePlace : placesByName.get(workspaceName) ?? null;

/**
 * The folder a workspace's files are written to: its place under the account's folder, or, when
 * its place is not known, that folder's top folder named after the workspace. The signed-in
 * account's folder when `userId` is left out.
 */
export const localWorkspaceDir = (workspaceName: string, userId?: string): string[] =>
    [...accountDir(userId), ...(placeFor(workspaceName)?.dir ?? [sanitizeName(workspaceName)])];

/**
 * Every folder a workspace's files may be in, in the order to look: its place, where the app
 * last put its folder, the last few folders it wrote to, its place at the top (from before
 * accounts had folders of their own) and the top folder named after it (where files were saved
 * before workspaces had places, and where they stay where folders cannot be moved).
 */
const workspaceDirs = (workspaceName: string, userId?: string): string[][] => {
    const place = placeFor(workspaceName);
    const top = accountDir(userId);
    const own = place?.dir ?? [sanitizeName(workspaceName)];
    const dirs = [[...top, ...own]];
    const before = place ? rememberedPlaces(userId)[place.id] : undefined;
    if (before) dirs.push(before.split('/'));
    if (place) for (const dir of pastPlaces(userId)[place.id] ?? []) dirs.push(dir.split('/'));
    if (top.length > 0) dirs.push(own);
    dirs.push([sanitizeName(workspaceName)]);
    const seen = new Set<string>();
    return dirs.filter(dir => !seen.has(placeKey(dir)) && !!seen.add(placeKey(dir)));
};

/**
 * Places of workspaces whose name matches a cloud path's workspace segment (cleaned differently):
 * under the account's folder, and at the top, where they were before accounts had folders of their own
 */
const placesMatching = (storageWorkspace: string, userId?: string): string[][] => {
    const key = workspaceFolderKey(storageWorkspace);
    const top = accountDir(userId);
    const dirs: string[][] = [];
    const add = (dir: string[]) => {
        dirs.push([...top, ...dir]);
        if (top.length > 0) dirs.push(dir);
    };
    if (openWorkspacePlace && workspaceFolderKey(openWorkspacePlace.name) === key) add(openWorkspacePlace.dir);
    for (const place of placesByName.values()) if (place && workspaceFolderKey(place.name) === key) add(place.dir);
    return dirs;
};

/** A folder inside another by its path: made on the way when `create`, else NotFoundError where one is missing */
const openDir = async (root: FileSystemDirectoryHandle, dir: string[], create = false): Promise<FileSystemDirectoryHandle> => {
    let current = root;
    for (const name of dir) current = await current.getDirectoryHandle(name, create ? { create: true } : undefined);
    return current;
};

// Convert base64/data URL to Blob
const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
    const response = await fetch(dataUrl);
    return await response.blob();
};

// Convert blob URL to Blob
const blobUrlToBlob = async (blobUrl: string): Promise<Blob> => {
    const response = await fetch(blobUrl);
    return await response.blob();
};

/**
 * Generate a consistent filename for both local and cloud saves
 */
export const generateSaveFilename = (
    category: SaveCategory,
    extension: string,
    timestamp?: number
): string => {
    // The cloud copy's name for the same time (utils/mediaNames.ts): lookups by it find this copy
    return mediaFileName(category, timestamp || Date.now(), extension);
};

/**
 * Save a file locally with organized folder structure
 */
export const saveFileLocally = async (
    directoryHandle: FileSystemDirectoryHandle,
    workspaceName: string,
    category: SaveCategory,
    fileData: string | Blob, // data URL, blob URL, or Blob
    fileExtension: string = 'png',
    customFilename?: string, // Optional: use this filename instead of generating one
    userId?: string // Whose folder it goes under: the signed-in account's when left out
): Promise<{ success: boolean; path: string; filename: string; error?: string }> => {
    try {
        // Verify we have permission
        const hasPermission = await verifyPermission(directoryHandle);
        if (!hasPermission) {
            return { success: false, path: '', filename: '', error: 'Permission denied' };
        }

        // Create folder structure: {root}/{Account}/{Project}/{Folder…}/{WorkspaceName}/{Category}/
        const workspaceFolder = localWorkspaceDir(workspaceName, userId);
        const workspaceDir = await openDir(directoryHandle, workspaceFolder, true);
        const categoryDir = await getOrCreateDirectory(workspaceDir, category);
        
        // Use custom filename or generate one
        const filename = customFilename || generateSaveFilename(category, fileExtension);
        
        // Convert to Blob if needed
        let blob: Blob;
        if (fileData instanceof Blob) {
            blob = fileData;
        } else if (fileData.startsWith('data:')) {
            blob = await dataUrlToBlob(fileData);
        } else if (fileData.startsWith('blob:')) {
            blob = await blobUrlToBlob(fileData);
        } else {
            return { success: false, path: '', filename: '', error: 'Invalid file data format' };
        }
        
        // Create and write file
        const fileHandle = await categoryDir.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        // The file is new or replaced: what was found under this name before is no longer true
        forgetLocated(category, filename);

        const path = `${placeKey(workspaceFolder)}/${category}/${filename}`;
        console.log(`✅ Auto-saved locally: ${path}`);
        
        return { success: true, path, filename };
        
    } catch (e: any) {
        console.error('Local save failed:', e);
        return { success: false, path: '', filename: '', error: e.message };
    }
};

/**
 * Get the current auto-save settings (scoped to userId)
 */
export const getLocalSaveSettings = async (userId?: string): Promise<LocalSaveSettings> => {
    const enabled = getUserLocalStorage(userId, 'autoSaveLocally') === 'true';
    const handle = await getStoredDirectoryHandle(userId);
    const lastUsedPath = getUserLocalStorage(userId, 'autoSaveLastPath');
    
    return {
        enabled: enabled && handle !== null,
        directoryHandle: handle,
        lastUsedPath,
    };
};

/**
 * Update auto-save enabled setting (scoped to userId)
 */
export const setAutoSaveEnabled = (enabled: boolean, userId?: string): void => {
    setUserLocalStorage(userId, 'autoSaveLocally', enabled ? 'true' : 'false');
    announceFolderChange(true);
};

/**
 * Check if auto-save is available and enabled (scoped to userId)
 */
export const isAutoSaveReady = async (userId?: string): Promise<boolean> => {
    if (!isFileSystemAccessSupported()) {
        console.log('🔍 Auto-save: File System Access API not supported (need localhost or HTTPS)');
        return false;
    }
    
    const settings = await getLocalSaveSettings(userId);
    if (!settings.enabled) {
        console.log('🔍 Auto-save: Not enabled in settings');
        return false;
    }
    if (!settings.directoryHandle) {
        console.log('🔍 Auto-save: No directory selected');
        return false;
    }
    
    // Verify permission is still valid
    const hasPermission = await verifyPermission(settings.directoryHandle);
    if (!hasPermission) {
        console.log('🔍 Auto-save: Permission denied for directory');
        return false;
    }
    
    return true;
};

/**
 * Quick save - uses stored settings (scoped to userId)
 * @param customFilename Optional: use this filename instead of generating one (for matching cloud filenames)
 */
export const autoSaveFile = async (
    workspaceName: string,
    category: SaveCategory,
    fileData: string | Blob,
    fileExtension: string = 'png',
    customFilename?: string,
    userId?: string
): Promise<{ success: boolean; path: string; filename: string; error?: string }> => {
    const settings = await getLocalSaveSettings(userId);
    
    if (!settings.enabled || !settings.directoryHandle) {
        return { success: false, path: '', filename: '', error: 'Auto-save not configured' };
    }

    const result = await saveFileLocally(
        settings.directoryHandle,
        workspaceName,
        category,
        fileData,
        fileExtension,
        customFilename,
        userId
    );
    if (result.success) notePlaceUsed(workspaceName, userId);
    return result;
};

// ============================================
// FILE BROWSING AND DOWNLOAD FUNCTIONS
// ============================================

export interface LocalFileInfo {
    name: string;
    path: string;
    type: 'file' | 'folder';
    size?: number;
    lastModified?: number;
    handle?: FileSystemFileHandle;
    children?: LocalFileInfo[];
}

/**
 * A file's size and date. The desktop app's handle tells them without reading the file (its
 * getFile reads all of it); a browser's File is only a description until its bytes are asked for.
 */
const fileInfoOf = async (handle: FileSystemFileHandle): Promise<{ size: number; lastModified: number }> => {
    if ((handle as unknown) instanceof DesktopFileHandle) return (handle as unknown as DesktopFileHandle).info();
    const file = await handle.getFile();
    return { size: file.size, lastModified: file.lastModified };
};

/**
 * Recursively list all files and folders in a directory
 */
const listDirectoryRecursive = async (
    dirHandle: FileSystemDirectoryHandle,
    basePath: string = ''
): Promise<LocalFileInfo[]> => {
    const items: LocalFileInfo[] = [];

    for await (const entry of (dirHandle as any).values()) {
        const entryPath = basePath ? `${basePath}/${entry.name}` : entry.name;

        if (entry.kind === 'directory') {
            // The desktop app's listing already gave the handle; asking for it again is one more call each
            const subDirHandle = entry instanceof DesktopDirectoryHandle
                ? entry as unknown as FileSystemDirectoryHandle
                : await dirHandle.getDirectoryHandle(entry.name);
            const children = await listDirectoryRecursive(subDirHandle, entryPath);
            items.push({
                name: entry.name,
                path: entryPath,
                type: 'folder',
                children,
            });
        } else {
            const fileHandle = entry instanceof DesktopFileHandle
                ? entry as unknown as FileSystemFileHandle
                : await dirHandle.getFileHandle(entry.name);
            const { size, lastModified } = await fileInfoOf(fileHandle);
            items.push({
                name: entry.name,
                path: entryPath,
                type: 'file',
                size,
                lastModified,
                handle: fileHandle,
            });
        }
    }
    
    // Sort: folders first, then files, alphabetically
    return items.sort((a, b) => {
        if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
        return a.name.localeCompare(b.name);
    });
};

/**
 * List all files in the auto-save directory
 */
export const listLocalSaveFiles = async (userId?: string): Promise<{
    success: boolean;
    files: LocalFileInfo[];
    rootName: string;
    error?: string;
}> => {
    const settings = await getLocalSaveSettings(userId);
    
    if (!settings.directoryHandle) {
        return { success: false, files: [], rootName: '', error: 'No folder selected' };
    }
    
    const hasPermission = await verifyPermission(settings.directoryHandle, false);
    if (!hasPermission) {
        return { success: false, files: [], rootName: '', error: 'Permission denied' };
    }
    
    try {
        const files = await listDirectoryRecursive(settings.directoryHandle);
        return {
            success: true,
            files,
            rootName: settings.directoryHandle.name,
        };
    } catch (e: any) {
        return { success: false, files: [], rootName: '', error: e.message };
    }
};

/**
 * Download a single file from the auto-save directory
 */
export const downloadLocalFile = async (fileHandle: FileSystemFileHandle): Promise<void> => {
    const file = await fileHandle.getFile();
    const url = URL.createObjectURL(file);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
    
    URL.revokeObjectURL(url);
};

/**
 * Download all files as a ZIP (requires JSZip library - fallback to individual downloads)
 */
export const downloadAllFilesAsZip = async (): Promise<{
    success: boolean;
    error?: string;
}> => {
    const { success, files, rootName, error } = await listLocalSaveFiles();
    
    if (!success || !files.length) {
        return { success: false, error: error || 'No files to download' };
    }
    
    // Collect all files recursively
    const collectFiles = (items: LocalFileInfo[]): LocalFileInfo[] => {
        const allFiles: LocalFileInfo[] = [];
        for (const item of items) {
            if (item.type === 'file') {
                allFiles.push(item);
            } else if (item.children) {
                allFiles.push(...collectFiles(item.children));
            }
        }
        return allFiles;
    };
    
    const allFiles = collectFiles(files);
    
    if (allFiles.length === 0) {
        return { success: false, error: 'No files found' };
    }
    
    // Try to use JSZip if available, otherwise download files individually
    if (typeof (window as any).JSZip !== 'undefined') {
        try {
            const JSZip = (window as any).JSZip;
            const zip = new JSZip();
            
            for (const fileInfo of allFiles) {
                if (fileInfo.handle) {
                    const file = await fileInfo.handle.getFile();
                    const arrayBuffer = await file.arrayBuffer();
                    zip.file(fileInfo.path, arrayBuffer);
                }
            }
            
            const blob = await zip.generateAsync({ type: 'blob' });
            const url = URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = `${rootName}_backup_${Date.now()}.zip`;
            link.click();
            
            URL.revokeObjectURL(url);
            return { success: true };
        } catch (e: any) {
            console.error('ZIP creation failed:', e);
        }
    }
    
    // Fallback: Download first 10 files individually with a delay
    console.log('JSZip not available, downloading files individually...');
    const maxFiles = Math.min(allFiles.length, 10);
    
    for (let i = 0; i < maxFiles; i++) {
        const fileInfo = allFiles[i];
        if (fileInfo.handle) {
            await downloadLocalFile(fileInfo.handle);
            // Small delay between downloads
            await new Promise(r => setTimeout(r, 300));
        }
    }
    
    if (allFiles.length > 10) {
        return { 
            success: true, 
            error: `Downloaded first 10 files. ${allFiles.length - 10} more files available.` 
        };
    }
    
    return { success: true };
};

/**
 * Get total size and file count stats
 */
export const getLocalSaveStats = async (userId?: string): Promise<{
    totalFiles: number;
    totalSize: number;
    workspaces: string[];
}> => {
    const { success, files } = await listLocalSaveFiles(userId);
    
    if (!success) {
        return { totalFiles: 0, totalSize: 0, workspaces: [] };
    }
    
    let totalFiles = 0;
    let totalSize = 0;
    const workspaces: string[] = [];
    
    const countFiles = (items: LocalFileInfo[]) => {
        for (const item of items) {
            if (item.type === 'file') {
                totalFiles++;
                totalSize += item.size || 0;
            } else {
                // Top-level folders are workspaces
                if (!item.path.includes('/')) {
                    workspaces.push(item.name);
                }
                if (item.children) {
                    countFiles(item.children);
                }
            }
        }
    };
    
    countFiles(files);
    
    return { totalFiles, totalSize, workspaces };
};

// ============================================
// CLOUD TO LOCAL SYNC
// ============================================

export interface CloudSyncProgress {
    total: number;
    completed: number;
    failed: number;
    currentFile: string;
}

/**
 * Download all files from Firebase Storage to local auto-save folder
 * Preserves the folder structure: {workspace}/{category}/{filename}
 */
export const syncCloudToLocal = async (
    userId: string,
    files: Array<{
        id: string;
        displayName: string;
        storagePath: string;
        downloadUrl: string;
        category?: string;
        workspaceName?: string;
        contentType?: string;
        size?: number;
    }>,
    onProgress?: (progress: CloudSyncProgress) => void
): Promise<{
    success: boolean;
    downloaded: number;
    failed: number;
    errors: string[];
}> => {
    const settings = await getLocalSaveSettings();
    
    if (!settings.directoryHandle) {
        return { success: false, downloaded: 0, failed: 0, errors: ['No local folder selected'] };
    }
    
    const hasPermission = await verifyPermission(settings.directoryHandle);
    if (!hasPermission) {
        return { success: false, downloaded: 0, failed: 0, errors: ['Permission denied to local folder'] };
    }
    
    let downloaded = 0;
    let failed = 0;
    const errors: string[] = [];
    
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        onProgress?.({
            total: files.length,
            completed: downloaded + failed,
            failed,
            currentFile: file.displayName,
        });
        
        try {
            // Fetch the file from Firebase Storage
            const response = await fetch(file.downloadUrl);
            if (!response.ok) {
                throw new Error(`Failed to download: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            
            // Determine folder structure
            const workspaceName = sanitizeName(file.workspaceName || 'Cloud_Backup');
            const category = file.category || 'Files';
            
            // Get or create directory structure
            const workspaceDir = await getOrCreateDirectory(settings.directoryHandle, workspaceName);
            const categoryDir = await getOrCreateDirectory(workspaceDir, category);
            
            // Create the file
            const filename = file.displayName || `file_${file.id}`;
            const fileHandle = await categoryDir.getFileHandle(filename, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            forgetLocated(category, filename);

            downloaded++;
            console.log(`✅ Downloaded: ${workspaceName}/${category}/${filename}`);
            
        } catch (e: any) {
            failed++;
            const errorMsg = `Failed to download ${file.displayName}: ${e.message}`;
            errors.push(errorMsg);
            console.error(errorMsg);
        }
    }
    
    onProgress?.({
        total: files.length,
        completed: downloaded + failed,
        failed,
        currentFile: 'Complete',
    });
    
    return {
        success: failed === 0,
        downloaded,
        failed,
        errors,
    };
};

// ============================================
// WORKSPACE LOCAL AUTO-SAVE
// ============================================

export interface WorkspaceData {
    nodes: any[];
    connections: any[];
    history: any[];
    transform: { x: number; y: number; scale: number };
    projectId: string;
    projectName: string;
    savedAt: number;
    version: string;
    localFileMap?: Record<string, string>; // Maps file:// references to local paths
    thumbnail?: string; // Base64 encoded thumbnail image
    /**
     * A cloud workspace's file: true when this copy holds work the cloud has not taken (it was
     * saved while the cloud save did not get through). Opened again, such a copy is the newer
     * one whatever the cloud's date says (see localCopyIsNewer in canvasSync.ts). False when
     * the cloud took this very copy: it is then never the newer one, whatever its own date
     * says. Missing in files written before this was recorded or by a save that did not try
     * the cloud (their date decides), and in a local workspace's file.
     */
    cloudPending?: boolean;
}

/**
 * Whether a workspace file read from the folder is workspace `projectId`'s own. The file is
 * found by the workspace's name, and two workspaces may have the same name (or names that
 * make the same folder name): the other one's file must not be opened as this one.
 */
export const isWorkspaceFileOf = (data: { projectId?: unknown } | null | undefined, projectId: string): boolean =>
    !!data && !!projectId && data.projectId === projectId;

// ============================================
// CUSTOM COMFYUI WORKFLOWS
// ============================================

export interface CustomWorkflow {
    filename: string;
    name: string;
    json: any;
}

/**
 * Get (and optionally create) the workflows/ directory within the selected local folder
 */
export const getWorkflowsDirectory = async (
    createIfMissing: boolean = true
): Promise<FileSystemDirectoryHandle | null> => {
    const settings = await getLocalSaveSettings();
    if (!settings.directoryHandle) return null;

    // Need read/write permission only when we might create folders
    const hasPermission = await verifyPermission(settings.directoryHandle, createIfMissing);
    if (!hasPermission) return null;

    try {
        if (createIfMissing) {
            return await getOrCreateDirectory(settings.directoryHandle, 'workflows');
        }
        return await settings.directoryHandle.getDirectoryHandle('workflows');
    } catch (e) {
        if (!createIfMissing) {
            return null; // Directory simply doesn't exist yet
        }
        console.error('Failed to get workflows directory:', e);
        return null;
    }
};

/**
 * Scan the workflows/ directory for .json files and return their parsed content
 */
export const listLocalWorkflows = async (): Promise<CustomWorkflow[]> => {
    const settings = await getLocalSaveSettings();
    if (!settings.directoryHandle) return [];

    const hasPermission = await verifyPermission(settings.directoryHandle, false);
    if (!hasPermission) return [];

    try {
        let workflowsDir: FileSystemDirectoryHandle;
        try {
            workflowsDir = await settings.directoryHandle.getDirectoryHandle('workflows');
        } catch {
            return [];
        }

        const workflows: CustomWorkflow[] = [];
        for await (const entry of (workflowsDir as any).values()) {
            if (entry.kind !== 'file' || !entry.name.toLowerCase().endsWith('.json')) {
                continue;
            }

            try {
                const fileHandle = await workflowsDir.getFileHandle(entry.name);
                const file = await fileHandle.getFile();
                const text = await file.text();
                const json = JSON.parse(text);

                workflows.push({
                    filename: entry.name,
                    name: entry.name.replace(/\.json$/i, ''),
                    json,
                });
            } catch (err) {
                console.warn(`Failed to load workflow ${entry.name}:`, err);
            }
        }

        // Sort alphabetically for predictable ordering
        workflows.sort((a, b) => a.name.localeCompare(b.name));
        return workflows;
    } catch (e) {
        console.error('Error listing workflows:', e);
        return [];
    }
};

/**
 * Save workspace data (project JSON) to the local auto-save folder
 * Saves to: {LocalFolder}/{WorkspaceName}/workspace.json
 */
export const saveWorkspaceLocally = async (
    workspaceName: string,
    projectData: {
        nodes: any[];
        connections: any[];
        history: any[];
        transform: { x: number; y: number; scale: number };
        projectId: string;
        projectName: string;
        thumbnail?: string;
        /** See WorkspaceData. */
        cloudPending?: boolean;
    },
    localFileMap?: Record<string, string>, // Optional pre-built file map (file:// ref -> local path)
    userId?: string
): Promise<{ success: boolean; path: string; error?: string }> => {
    const settings = await getLocalSaveSettings(userId);
    
    if (!settings.directoryHandle) {
        return { success: false, path: '', error: 'No local folder selected' };
    }
    
    const hasPermission = await verifyPermission(settings.directoryHandle);
    if (!hasPermission) {
        return { success: false, path: '', error: 'Permission denied' };
    }
    
    try {
        // Create workspace folder if it doesn't exist (at the workspace's place)
        const workspaceFolder = localWorkspaceDir(workspaceName, userId);
        const sanitizedWorkspace = workspaceFolder[workspaceFolder.length - 1];
        const workspaceDir = await openDir(settings.directoryHandle, workspaceFolder, true);
        
        // Prepare workspace data with metadata
        const workspaceData: WorkspaceData = {
            ...projectData,
            savedAt: Date.now(),
            version: '1.0',
            localFileMap: localFileMap && Object.keys(localFileMap).length > 0 ? localFileMap : undefined,
        };
        
        // Convert to JSON. An address a local file is shown by dies with the session, and in
        // the desktop app it holds that run's id. Where a view left one in the data, the file
        // reference it stands for is written instead (nothing when that is not known), and
        // the id never reaches the disk wherever in the data an address sits.
        const jsonContent = withoutLocalMediaSecrets(JSON.stringify(workspaceData, (_key, value) => {
            if (typeof value !== 'string') return value;
            if (isLocalMediaUrl(value)) return referenceOfLocalUrl(value) ?? '';
            return value.startsWith('blob:') ? referenceOfLocalUrl(value) ?? value : value;
        }, 2));
        const blob = new Blob([jsonContent], { type: 'application/json' });
        
        // Save {WorkspaceName}.json (named after the workspace)
        const filename = `${sanitizedWorkspace}.json`;
        const fileHandle = await workspaceDir.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        
        const path = `${placeKey(workspaceFolder)}/${filename}`;
        const mapCount = localFileMap ? Object.keys(localFileMap).length : 0;
        console.log(`✅ Workspace saved locally: ${path}${mapCount > 0 ? ` (${mapCount} file mappings)` : ''}`);
        notePlaceUsed(workspaceName, userId);
        
        return { success: true, path };
        
    } catch (e: any) {
        console.error('Local workspace save failed:', e);
        return { success: false, path: '', error: e.message };
    }
};

/**
 * Check if workspace auto-save is enabled (scoped to userId)
 */
export const isWorkspaceAutoSaveEnabled = (userId?: string): boolean => {
    return getUserLocalStorage(userId, 'autoSaveWorkspace') === 'true';
};

/**
 * Set workspace auto-save enabled/disabled (scoped to userId)
 */
export const setWorkspaceAutoSaveEnabled = (enabled: boolean, userId?: string): void => {
    setUserLocalStorage(userId, 'autoSaveWorkspace', enabled ? 'true' : 'false');
};

/**
 * Renames a workspace's folder on disk in one step, and the workspace file inside it, where
 * the folder is the desktop app's. False when that cannot be done here (a browser's folder,
 * or the new name is already taken): the caller then copies the files.
 */
const renameFolderOnDisk = async (
    root: FileSystemDirectoryHandle,
    oldDir: FileSystemDirectoryHandle,
    oldName: string,
    newName: string
): Promise<boolean> => {
    const folder = root as unknown;
    const source = oldDir as unknown;
    if (!(folder instanceof DesktopDirectoryHandle) || !(source instanceof DesktopDirectoryHandle)) return false;
    try {
        await source.moveTo(folder, newName);
    } catch {
        return false;
    }
    try {
        const renamed = await folder.getDirectoryHandle(newName);
        // The workspace's own file is named after it (older ones are workspace.json)
        for (const jsonName of [`${oldName}.json`, 'workspace.json']) {
            const moved = await renamed.getFileHandle(jsonName).then(file => file.moveTo(renamed, `${newName}.json`)).then(() => true, () => false);
            if (moved) break;
        }
    } catch (e) {
        console.warn('The workspace file could not be renamed with its folder:', e);
    }
    return true;
};

/**
 * Rename a workspace folder locally
 * The desktop app renames it on disk. The File System Access API doesn't support direct rename, so in a browser we:
 * 1. Create new folder with new name
 * 2. Copy all files from old folder to new
 * 3. Delete old folder
 */
export const renameWorkspaceFolder = async (
    oldName: string,
    newName: string,
    userId?: string
): Promise<{ success: boolean; error?: string }> => {
    const settings = await getLocalSaveSettings(userId);
    
    if (!settings.directoryHandle) {
        return { success: false, error: 'No local folder selected' };
    }
    
    const hasPermission = await verifyPermission(settings.directoryHandle);
    if (!hasPermission) {
        return { success: false, error: 'Permission denied' };
    }
    
    const sanitizedOldName = sanitizeName(oldName);
    const sanitizedNewName = sanitizeName(newName);
    
    // If names are the same after sanitization, nothing to do
    if (sanitizedOldName === sanitizedNewName) {
        return { success: true };
    }
    
    try {
        // The workspace's folder: right under the account's folder, else at the top (from before accounts had folders of their own)
        const top = accountDir(userId);
        let parentDir: FileSystemDirectoryHandle | null = null;
        let oldDir: FileSystemDirectoryHandle | null = null;
        for (const parent of top.length > 0 ? [top, []] : [[]]) {
            try {
                const dir = await openDir(settings.directoryHandle, parent);
                oldDir = await dir.getDirectoryHandle(sanitizedOldName);
                parentDir = dir;
                break;
            } catch { /* not there */ }
        }
        if (!parentDir || !oldDir) {
            // Old folder doesn't exist, nothing to rename
            console.log(`Folder "${sanitizedOldName}" doesn't exist, skipping rename`);
            return { success: true };
        }

        // The desktop app renames the folder on disk in one step. A browser's folder API cannot,
        // so there (and when the new name is already a folder) the files are copied over.
        if (await renameFolderOnDisk(parentDir, oldDir, sanitizedOldName, sanitizedNewName)) {
            forgetLocated();
            console.log(`✅ Workspace folder renamed: ${sanitizedOldName} -> ${sanitizedNewName}`);
            return { success: true };
        }

        // Names that differ only in upper and lower case are one folder on Windows and macOS:
        // copying it onto itself and then removing the "old" one would remove everything
        if (sanitizedOldName.toLowerCase() === sanitizedNewName.toLowerCase()) return { success: true };

        // Create new folder
        const newDir = await getOrCreateDirectory(parentDir, sanitizedNewName);

        // Recursively copy all contents, renaming the workspace JSON file
        await copyDirectoryContents(oldDir, newDir, {
            oldName: sanitizedOldName,
            newName: sanitizedNewName
        });

        // Delete old folder
        await parentDir.removeEntry(sanitizedOldName, { recursive: true });
        forgetLocated();

        console.log(`✅ Workspace folder renamed: ${sanitizedOldName} -> ${sanitizedNewName}`);
        return { success: true };
        
    } catch (e: any) {
        console.error('Workspace folder rename failed:', e);
        return { success: false, error: e.message };
    }
};

/**
 * Helper: Recursively copy directory contents
 * @param renameJsonFile - If provided, renames workspace JSON files from old name to new name
 */
const copyDirectoryContents = async (
    source: FileSystemDirectoryHandle,
    destination: FileSystemDirectoryHandle,
    renameJsonFile?: { oldName: string; newName: string }
): Promise<void> => {
    for await (const entry of (source as any).values()) {
        if (entry.kind === 'file') {
            const fileHandle = await source.getFileHandle(entry.name);
            const file = await fileHandle.getFile();
            
            // Check if this is the workspace JSON file that needs renaming
            let newFileName = entry.name;
            if (renameJsonFile) {
                const oldJsonName = `${renameJsonFile.oldName}.json`;
                const legacyJsonName = 'workspace.json';
                if (entry.name === oldJsonName || entry.name === legacyJsonName) {
                    newFileName = `${renameJsonFile.newName}.json`;
                }
            }
            
            const newFileHandle = await destination.getFileHandle(newFileName, { create: true });
            const writable = await newFileHandle.createWritable();
            await writable.write(file);
            await writable.close();
        } else if (entry.kind === 'directory') {
            const subDir = await source.getDirectoryHandle(entry.name);
            const newSubDir = await destination.getDirectoryHandle(entry.name, { create: true });
            await copyDirectoryContents(subDir, newSubDir);
        }
    }
};

/**
 * Load workspace data from local storage
 */
export const loadWorkspaceLocally = async (
    workspaceName: string,
    userId?: string
): Promise<{ success: boolean; data?: WorkspaceData; error?: string }> => {
    const settings = await getLocalSaveSettings(userId);
    
    if (!settings.directoryHandle) {
        return { success: false, error: 'No local folder selected' };
    }
    
    const hasPermission = await verifyPermission(settings.directoryHandle, false);
    if (!hasPermission) {
        return { success: false, error: 'Permission denied' };
    }
    
    // The workspace's place first, then where its folder was before (see workspaceDirs)
    let lastError = 'The workspace has no file in the local folder';
    for (const dir of workspaceDirs(workspaceName, userId)) {
        try {
            const result = await loadWorkspaceAt(settings.directoryHandle, dir, sanitizeName(workspaceName));
            if (result) return { success: true, data: result };
        } catch (e: any) {
            lastError = e?.message || lastError;
        }
    }
    return { success: false, error: lastError };
};

/**
 * The workspace file of the folder at `dir`, or null when it has none: named after its folder,
 * or after the workspace (a folder moved under another name keeps its file's name for a moment),
 * or workspace.json (older ones).
 */
const loadWorkspaceAt = async (root: FileSystemDirectoryHandle, dir: string[], workspaceFolderName?: string): Promise<WorkspaceData | null> => {
    const workspaceDir = await openDir(root, dir);
    const names = [...new Set([`${dir[dir.length - 1]}.json`, ...(workspaceFolderName ? [`${workspaceFolderName}.json`] : []), 'workspace.json'])];
    for (const name of names) {
        const file = await workspaceDir.getFileHandle(name).then(handle => handle.getFile(), () => null);
        if (file) return JSON.parse(await file.text()) as WorkspaceData;
    }
    return null;
};

/**
 * The workspace files in the local folder, found by walking it (a few levels: projects and
 * folders hold workspaces), as folder paths. For bringing workspaces in from the folder. The
 * account's own folder is walked first, then the top (where folders were before accounts had
 * folders of their own), leaving out the app's own folders and those of other accounts that
 * used this device.
 */
export const findLocalWorkspaceFolders = async (userId?: string, maxDepth = 6): Promise<string[][]> => {
    const settings = await getLocalSaveSettings(userId);
    const root = settings.directoryHandle;
    if (!root || !(await verifyPermission(root, false, false))) return [];
    const top = accountDir(userId);
    const leftOut = new Set(['workflows', ...top.slice(0, 1), ...otherAccounts(userId).folders]);
    const found: string[][] = [];
    const walk = async (dir: FileSystemDirectoryHandle, path: string[], depth: number): Promise<void> => {
        const files = new Set<string>();
        const folders: [string, FileSystemDirectoryHandle][] = [];
        try {
            for await (const [name, entry] of (dir as any).entries() as AsyncIterable<[string, FileSystemHandle]>) {
                if (entry.kind === 'file') files.add(name);
                else folders.push([name, entry as FileSystemDirectoryHandle]);
            }
        } catch {
            return;
        }
        if (path.length > 0 && (files.has(`${path[path.length - 1]}.json`) || files.has('workspace.json'))) {
            found.push(path);
            return; // a workspace's own folders are its media
        }
        if (depth >= maxDepth) return;
        for (const [name, child] of folders) if (path.length > 0 || !leftOut.has(name)) await walk(child, [...path, name], depth + 1);
    };
    if (top.length > 0) {
        try {
            await walk(await openDir(root, top), top, 0);
        } catch { /* the account has no folder of its own yet */ }
    }
    await walk(root, [], 0);
    return found;
};

/** The workspace file in the folder at `dir` (from findLocalWorkspaceFolders) */
export const loadWorkspaceLocallyAt = async (dir: string[], userId?: string): Promise<{ success: boolean; data?: WorkspaceData; error?: string }> => {
    const settings = await getLocalSaveSettings(userId);
    if (!settings.directoryHandle) return { success: false, error: 'No local folder selected' };
    if (!(await verifyPermission(settings.directoryHandle, false))) return { success: false, error: 'Permission denied' };
    try {
        const data = await loadWorkspaceAt(settings.directoryHandle, dir);
        return data ? { success: true, data } : { success: false, error: 'No workspace file there' };
    } catch (e: any) {
        return { success: false, error: e?.message || 'The workspace file could not be read' };
    }
};

/**
 * The workspace segment of a cloud storage path (users/{uid}/{workspace}/{category}/{file}):
 * the workspace's name, as cleaned for the cloud, when the file was first uploaded.
 */
export const storageWorkspaceOf = (storagePath: string | undefined | null): string | undefined => {
    const parts = typeof storagePath === 'string' ? storagePath.split('/') : [];
    return parts.length >= 5 && parts[0] === 'users' ? parts[parts.length - 3] : undefined;
};

/** Workspace folder names reduced to letters and digits, so a local folder can be matched to a cloud path. */
const workspaceFolderKey = (name: string): string =>
    name.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase();

/** The workspace folders under the account's folder and at the top, by key, read at most once a minute. */
let folderIndex: { root: FileSystemDirectoryHandle; account: string; readAt: number; dirs: Promise<Map<string, string[]>> } | null = null;

const findWorkspaceFolder = async (root: FileSystemDirectoryHandle, storageWorkspace: string, userId?: string): Promise<string[] | null> => {
    const top = accountDir(userId);
    const account = placeKey(top);
    if (!folderIndex || folderIndex.root !== root || folderIndex.account !== account || Date.now() - folderIndex.readAt > 60_000) {
        const dirs = (async () => {
            const byKey = new Map<string, string[]>();
            const index = async (under: string[]) => {
                try {
                    const dir = await openDir(root, under);
                    for await (const [name, entry] of (dir as any).entries() as AsyncIterable<[string, FileSystemHandle]>) {
                        if (entry.kind !== 'directory') continue;
                        const key = workspaceFolderKey(name);
                        if (!byKey.has(key)) byKey.set(key, [...under, name]);
                    }
                } catch { /* unreadable, or not there: no matches in it */ }
            };
            // The account's own folders first, then the top (where folders were before accounts had folders of their own)
            if (top.length > 0) await index(top);
            await index([]);
            return byKey;
        })();
        folderIndex = { root, account, readAt: Date.now(), dirs };
    }
    return (await folderIndex.dirs).get(workspaceFolderKey(storageWorkspace)) ?? null;
};

/**
 * A media file from the local auto-save folder, or null when no folder is set up,
 * auto-save is off, the browser has not given access in this session, or the file is
 * not there (an empty file, left by an interrupted save, counts as not there). It never
 * asks for access, which needs a click (requestLocalFolderAccess).
 *
 * The file is looked for in the current workspace's folder and then, when the cloud
 * path names another workspace (`storageWorkspace`), in that workspace's folder: the
 * file may have been saved before a rename, or in the workspace it was first made in.
 * In a browser the File is read from disk as it is used, so it costs no memory up front.
 * In the desktop app all of its bytes are fetched: this is for callers that need the bytes.
 * To show the file, ask locateLocalFile for an address instead.
 */
export const readLocalFile = async (
    workspaceName: string,
    category: string, // 'Generated_Images' | 'Videos' | 'Sketches' etc.
    filename: string,
    userId?: string,
    storageWorkspace?: string
): Promise<File | null> => {
    const root = await readableFolder(userId);
    return root ? readFromFolder(root, workspaceName, category, filename, storageWorkspace, userId) : null;
};

/**
 * The folders a file may be in, in the order to look: the workspace's (its place, where its
 * folder was before), then, when the cloud path names another workspace, that one's: at the top
 * under that name, and the place of a workspace with that name. At most 16, as the desktop
 * shell checks no more in one call.
 */
const candidateFolders = (workspaceName: string, storageWorkspace: string | undefined, userId?: string): string[][] => {
    const dirs = workspaceDirs(workspaceName, userId);
    if (storageWorkspace) {
        const top = accountDir(userId);
        const named = sanitizeName(storageWorkspace);
        dirs.push([...top, named]);
        if (top.length > 0) dirs.push([named]);
        dirs.push(...placesMatching(storageWorkspace, userId));
    }
    const seen = new Set<string>();
    return dirs.filter(dir => !seen.has(placeKey(dir)) && !!seen.add(placeKey(dir))).slice(0, 16);
};

/** The folder media may be read from right now, or null: none chosen, auto-save off, or access not given (it is never asked for). */
const readableFolder = async (userId?: string): Promise<FileSystemDirectoryHandle | null> => {
    const settings = await getLocalSaveSettings(userId);
    const root = settings.directoryHandle;
    if (!root || !settings.enabled) return null;
    return (await verifyPermission(root, false, false)) ? root : null;
};

/**
 * Where a media file is in the desktop app's folder, in one call to the shell: the current
 * workspace's folder, then the one the cloud path names. Only when neither has it and the
 * cloud path names a workspace is the root listed (kept a minute) to find a folder whose
 * name was cleaned differently. Null when the file is nowhere, or is empty.
 */
const locateOnDesktop = async (
    desktop: NoodleDesktop,
    root: DesktopDirectoryHandle,
    workspaceName: string,
    category: string,
    filename: string,
    storageWorkspace?: string,
    userId?: string
): Promise<DesktopLocated | null> => {
    const ask = async (folders: string[]): Promise<DesktopLocated | null> => {
        try {
            const answer = await desktop.folder.locate!(root.account, folders.map(folder => `${folder}/${category}/${filename}`));
            return answer.ok ? answer.value : null;
        } catch {
            return null;
        }
    };
    const folders = candidateFolders(workspaceName, storageWorkspace, userId).map(placeKey);
    const found = await ask(folders);
    if (found || !storageWorkspace) return found;
    const other = await findWorkspaceFolder(root as unknown as FileSystemDirectoryHandle, storageWorkspace, userId);
    return other && !folders.includes(placeKey(other)) ? ask([placeKey(other)]) : null;
};

const readFromFolder = async (
    root: FileSystemDirectoryHandle,
    workspaceName: string,
    category: string,
    filename: string,
    storageWorkspace?: string,
    userId?: string
): Promise<File | null> => {
    const desktop = desktopApp();
    const folder = root as unknown;
    if (desktop?.folder.locate && folder instanceof DesktopDirectoryHandle) {
        // The desktop app: one call finds the file, and its bytes come from the app's own server, not through the bridge
        const found = await locateOnDesktop(desktop, folder, workspaceName, category, filename, storageWorkspace, userId);
        if (!found) return null;
        try {
            const file = await new DesktopFileHandle(desktop, folder.account, found.rel, filename, found).getFile();
            return file.size > 0 ? file : null;
        } catch {
            return null; // gone since it was found
        }
    }

    const tried = new Set<string>();
    const readFrom = async (workspaceFolder: string[] | null): Promise<File | null> => {
        if (!workspaceFolder || tried.has(placeKey(workspaceFolder))) return null;
        tried.add(placeKey(workspaceFolder));
        try {
            const workspaceDir = await openDir(root, workspaceFolder);
            const categoryDir = await workspaceDir.getDirectoryHandle(category);
            const file = await (await categoryDir.getFileHandle(filename)).getFile();
            return file.size > 0 ? file : null;
        } catch {
            return null; // File doesn't exist there
        }
    };

    for (const dir of candidateFolders(workspaceName, storageWorkspace, userId)) {
        const file = await readFrom(dir);
        if (file) return file;
    }
    if (!storageWorkspace) return null;
    return readFrom(await findWorkspaceFolder(root, storageWorkspace, userId));
};

/**
 * A media file of the local folder as an address to show it from, found once per session:
 * every place that shows the file shares the lookup and the address, and opening the
 * workspace again looks nothing up. Null in the same cases as readLocalFile, and a miss is
 * not remembered.
 *
 * In the desktop app the address streams the file from disk through the app's own server
 * (`streamed`): one call to the shell finds it and no bytes are read until an element asks.
 * In a browser it is one object URL of the file, which the browser reads from disk as it is
 * used. Either way the address is for this session only and must never be saved.
 */
export const locateLocalFile = (
    workspaceName: string,
    category: string,
    filename: string,
    userId?: string,
    storageWorkspace?: string
): Promise<LocalMedia | null> => {
    const key = locatedKey(folderAccount(userId), sanitizeName(workspaceName), category, filename, storageWorkspace);
    const known = locatedFiles.get(key);
    if (known) {
        const found = foundFiles.get(key);
        if (found) usedObjectUrl(found.url);
        return known;
    }
    const pending: Promise<LocalMedia | null> = findLocalMedia(workspaceName, category, filename, userId, storageWorkspace).then((found) => {
        const media = found ? found.media : null;
        // The folder may have changed, or the file been rewritten, while it was looked for: the answer is then given but not kept
        const kept = locatedFiles.get(key) === pending;
        if (kept && !media) locatedFiles.delete(key);
        if (kept && media) {
            foundFiles.set(key, media);
            currentUrls.set(media.url, (currentUrls.get(media.url) || 0) + 1);
        }
        if (found && !found.media.streamed) {
            objectUrls.set(found.media.url, { key: kept ? key : null, holders: 0, usedAt: Date.now(), memory: found.memory });
            releaseUnusedObjectUrls();
        }
        return media;
    }, () => {
        if (locatedFiles.get(key) === pending) locatedFiles.delete(key);
        return null;
    });
    locatedFiles.set(key, pending);
    return pending;
};

/** The file as an address, and how many bytes are held in memory behind that address. */
const findLocalMedia = async (
    workspaceName: string,
    category: string,
    filename: string,
    userId?: string,
    storageWorkspace?: string
): Promise<{ media: LocalMedia; memory: number } | null> => {
    const root = await readableFolder(userId);
    if (!root) return null;
    const desktop = desktopApp();
    const folder = root as unknown;
    if (desktop?.folder.locate && desktop.mediaBase && folder instanceof DesktopDirectoryHandle) {
        const found = await locateOnDesktop(desktop, folder, workspaceName, category, filename, storageWorkspace, userId);
        if (!found) return null;
        const lastModified = Math.round(found.mtimeMs);
        const url = desktopMediaUrl(desktop, folder.account, found.rel, `${found.size}-${lastModified}`);
        if (url) return { media: { url, size: found.size, lastModified, streamed: true }, memory: 0 };
    }
    const file = await readFromFolder(root, workspaceName, category, filename, storageWorkspace, userId);
    if (!file) return null;
    return {
        media: { url: URL.createObjectURL(file), size: file.size, lastModified: file.lastModified, streamed: false },
        // A browser reads the file from disk as it is shown; the desktop app's bridge hands over all of its bytes
        memory: folder instanceof DesktopDirectoryHandle ? file.size : 0,
    };
};

/**
 * Whether an address locateLocalFile gave is still good: the folder has not changed since,
 * and the app has not written, moved or removed that file. A view that kept the address
 * asks before using it again.
 */
export const isCurrentLocalUrl = (url: string): boolean => currentUrls.has(url);

/**
 * Tells the cache that a view shows a local address, so the address is not released under
 * it; the returned function lets go. Only object URLs need holding (see objectUrls): for any
 * other address this does nothing.
 */
export const holdLocalUrl = (url: string | null | undefined): (() => void) => {
    const use = url ? objectUrls.get(url) : undefined;
    if (!url || !use) return () => undefined;
    use.holders++;
    usedObjectUrl(url);
    let held = true;
    return () => {
        if (!held) return;
        held = false;
        use.holders--;
        use.usedAt = Date.now();
        releaseUnusedObjectUrls();
    };
};

/** Forget where a file was found, after it was changed or removed outside this module; the next lookup reads the folder again. */
export const forgetLocalFile = (category: string, filename: string): void => forgetLocated(category, filename);

/** Forget the find behind an address that failed to load (the file was moved or removed since). */
export const forgetLocalUrl = (url: string): void => {
    let dropped = false;
    for (const [key, media] of Array.from(foundFiles)) {
        if (media.url !== url) continue;
        dropLocated(key);
        dropped = true;
    }
    if (dropped) {
        folderRevision++;
        releaseUnusedObjectUrls();
    }
};

/**
 * A number that goes up whenever the folder's contents or access to it change through the
 * app. A view that remembered "this file is not in the folder" compares it to know when to
 * look again.
 */
export const localFolderRevision = (): number => folderRevision;

/**
 * Moves a file to another folder and name on disk, where the folder is the desktop app's, so
 * nothing is copied through the page. An empty leftover under the new name is removed first.
 * False when it cannot be done here (a browser's folder): the caller then copies the bytes.
 */
const moveFileOnDisk = async (
    source: FileSystemFileHandle,
    targetDir: FileSystemDirectoryHandle,
    name: string,
    emptyLeftover: boolean
): Promise<boolean> => {
    const file = source as unknown;
    const dir = targetDir as unknown;
    if (!(file instanceof DesktopFileHandle) || !(dir instanceof DesktopDirectoryHandle)) return false;
    try {
        if (emptyLeftover) await dir.removeEntry(name);
        await file.moveTo(dir, name);
        return true;
    } catch {
        return false;
    }
};

/**
 * Move a file within the current workspace's folder to another category folder and name.
 * Used when an upload turned out to be a copy of a file already in the cloud under
 * another name: the local copy then takes that name, so lookups by the cloud name find
 * it. Needs write access already granted (it never asks). When the target is already
 * there, the source is only removed. Returns whether the target exists afterwards.
 */
export const moveLocalFile = async (
    workspaceName: string,
    from: { category: string; name: string },
    to: { category: string; name: string },
    userId?: string
): Promise<boolean> => {
    if (from.category === to.category && from.name === to.name) return true;
    const settings = await getLocalSaveSettings(userId);
    const root = settings.directoryHandle;
    if (!root || !settings.enabled) return false;
    if (!(await verifyPermission(root, true, false))) return false;
    try {
        // The folder the copy was saved in: the workspace's place, or where its folder was before
        let found: { workspaceDir: FileSystemDirectoryHandle; sourceDir: FileSystemDirectoryHandle; sourceHandle: FileSystemFileHandle } | null = null;
        for (const dir of workspaceDirs(workspaceName, userId)) {
            try {
                const workspaceDir = await openDir(root, dir);
                const sourceDir = await workspaceDir.getDirectoryHandle(from.category);
                found = { workspaceDir, sourceDir, sourceHandle: await sourceDir.getFileHandle(from.name) };
                break;
            } catch { /* not in this one */ }
        }
        if (!found) return false;
        const { workspaceDir, sourceDir, sourceHandle } = found;
        const sourceSize = (await fileInfoOf(sourceHandle)).size;
        if (sourceSize === 0) return false;
        const targetDir = await getOrCreateDirectory(workspaceDir, to.category);
        // What is under the target name now: nothing, an empty leftover, or a file of some size.
        // Anything unexpected (a file another program holds open) stops the move untouched.
        let targetSize = 0;
        let targetThere = true;
        try {
            targetSize = (await fileInfoOf(await targetDir.getFileHandle(to.name))).size;
        } catch (e) {
            if ((e as { name?: string })?.name !== 'NotFoundError') return false;
            targetThere = false;
        }
        if (targetSize > 0 && targetSize !== sourceSize) return false; // another file under that name: keep both
        if (targetSize > 0) {
            await sourceDir.removeEntry(from.name);
        } else if (!(await moveFileOnDisk(sourceHandle, targetDir, to.name, targetThere))) {
            const writable = await (await targetDir.getFileHandle(to.name, { create: true })).createWritable();
            await writable.write(await sourceHandle.getFile());
            await writable.close();
            await sourceDir.removeEntry(from.name);
        }
        forgetLocated(from.category, from.name);
        forgetLocated(to.category, to.name);
        return true;
    } catch (e) {
        console.warn('Could not move the local copy:', e);
        return false;
    }
};

/**
 * The same file as an address for elements that show it (see locateLocalFile, which also
 * gives its size and date). Null in the same cases as readLocalFile. The address is shared
 * by everything that shows the file and belongs to the session's cache: do not revoke it.
 */
export const resolveFileLocally = async (
    workspaceName: string,
    category: string,
    filename: string,
    userId?: string,
    storageWorkspace?: string
): Promise<string | null> => {
    const found = await locateLocalFile(workspaceName, category, filename, userId, storageWorkspace);
    return found ? found.url : null;
};

/**
 * Whether media can be read from the local auto-save folder right now.
 * - unsupported: this browser has no File System Access API
 * - none: no folder has been chosen in this browser
 * - off: a folder is chosen but auto-save is switched off in Settings
 * - granted: files can be read without asking
 * - prompt: the browser needs a click to allow access again, as after a restart
 * - denied: access was refused
 */
export type LocalFolderAccess =
    | { state: 'unsupported' | 'none' }
    | { state: 'off' | 'granted' | 'prompt' | 'denied'; name: string };

export const getLocalFolderAccess = async (userId?: string): Promise<LocalFolderAccess> => {
    if (!isFileSystemAccessSupported()) return { state: 'unsupported' };
    const settings = await getLocalSaveSettings(userId);
    const handle = settings.directoryHandle;
    if (!handle) return { state: 'none' };
    if (!settings.enabled) return { state: 'off', name: handle.name };
    try {
        const state = await (handle as any).queryPermission({ mode: 'read' });
        if (state === 'granted' || state === 'denied') return { state, name: handle.name };
    } catch (e) {
        console.warn('queryPermission failed:', e);
    }
    return { state: 'prompt', name: handle.name };
};

// ── Keeping folders where the library has them ───────────────────────────────

/** True when a folder has nothing in it */
const isEmptyDir = async (dir: FileSystemDirectoryHandle): Promise<boolean> => {
    for await (const _entry of (dir as any).keys() as AsyncIterable<string>) return false;
    return true;
};

/**
 * Everything in `source` that `target` has no entry of that name for goes over; folders both
 * have are merged the same way; a file of a name `target` already has stays where it is (it is
 * still found there). `source` is removed when it ends up empty. Windows will not rename a
 * folder while a program has a file in it open, nor remove one a program is sitting in: such a
 * folder's files go one by one, what will not go stays for next time, the rest still goes, and
 * the first such error is thrown at the end.
 */
const mergeFolder = async (sourceParent: DesktopDirectoryHandle, source: DesktopDirectoryHandle, target: DesktopDirectoryHandle): Promise<void> => {
    const there = new Map<string, 'file' | 'directory'>();
    for await (const [name, entry] of target.entries()) there.set(name, entry.kind);
    let failure: unknown = null;
    for await (const [name, entry] of source.entries()) {
        try {
            const kind = there.get(name);
            if (kind === 'directory' && entry instanceof DesktopDirectoryHandle) {
                await mergeFolder(source, entry, await target.getDirectoryHandle(name));
            } else if (!kind) {
                try {
                    await entry.moveTo(target, name);
                } catch (e) {
                    if (!(entry instanceof DesktopDirectoryHandle)) throw e;
                    await mergeFolder(source, entry, await target.getDirectoryHandle(name, { create: true }));
                }
            }
        } catch (e) {
            if (failure === null) failure = e;
        }
    }
    if (failure !== null) throw failure;
    if (await isEmptyDir(source as unknown as FileSystemDirectoryHandle)) await sourceParent.removeEntry(source.name);
};

/** One workspace's folder to its place, its workspace file renamed with it, and the folders it leaves empty removed */
const moveWorkspaceFolder = async (root: DesktopDirectoryHandle, from: string[], to: string[]): Promise<void> => {
    const sourceParent = (await openDir(root as unknown as FileSystemDirectoryHandle, from.slice(0, -1))) as unknown as DesktopDirectoryHandle;
    const source = await sourceParent.getDirectoryHandle(from[from.length - 1]);
    const parent = (await openDir(root as unknown as FileSystemDirectoryHandle, to.slice(0, -1), true)) as unknown as DesktopDirectoryHandle;
    const name = to[to.length - 1];
    const caseOnly = placeKey(from).toLowerCase() === placeKey(to).toLowerCase();
    const existing = caseOnly ? null : await parent.getDirectoryHandle(name).catch(() => null);
    // A folder already at the place (files saved there since) takes in what the old one has
    if (existing) {
        await mergeFolder(sourceParent, source, existing);
    } else {
        try {
            await source.moveTo(parent, name);
        } catch (e) {
            // Windows will not rename a folder while a file in it is open (a picture the open
            // workspace is loading, one open in another program): what can go goes file by
            // file, the rest stays until next time, and both places are looked at meanwhile
            if (caseOnly) throw e;
            await mergeFolder(sourceParent, source, await parent.getDirectoryHandle(name, { create: true }));
        }
    }

    // The workspace's own file is named after its folder
    const oldJson = `${from[from.length - 1]}.json`;
    const newJson = `${name}.json`;
    if (oldJson !== newJson) {
        const target = await parent.getDirectoryHandle(name);
        const names = new Set<string>();
        for await (const entryName of target.keys()) names.add(entryName);
        if (names.has(oldJson) && !names.has(newJson)) await (await target.getFileHandle(oldJson)).moveTo(target, newJson);
    }

    // The folders on the way that it leaves empty (a project or folder whose workspaces all moved) go
    for (let depth = from.length; depth >= 1; depth--) {
        const dir = from.slice(0, depth);
        if (to.length >= depth && placeKey(to.slice(0, depth)) === placeKey(dir)) break; // still on the way to the new place
        let handle: FileSystemDirectoryHandle;
        try {
            handle = await openDir(root as unknown as FileSystemDirectoryHandle, dir);
        } catch {
            continue; // moved away already
        }
        if (!(await isEmptyDir(handle))) break;
        const above = (await openDir(root as unknown as FileSystemDirectoryHandle, dir.slice(0, -1))) as unknown as DesktopDirectoryHandle;
        await above.removeEntry(dir[dir.length - 1]);
    }
};

let organising: Promise<unknown> = Promise.resolve();

/**
 * Moves workspaces' folders to where the library has them (utils/localPlaces.ts plans which,
 * and only when it is certain). Only the desktop app does it: there a move is one step on disk
 * and nothing is copied. A browser's folder cannot move folders, so there nothing is moved and
 * files are found where they are. Runs one at a time; a move that fails is tried again next time.
 */
export const organiseLocalFolder = (
    places: WorkspacePlace[],
    unplacedNames: string[],
    userId?: string,
): Promise<{ moved: FolderMove[]; failed: number }> => {
    const run = organising.then(async () => {
        const settings = await getLocalSaveSettings(userId);
        const root = settings.directoryHandle as unknown;
        if (!settings.enabled || !(root instanceof DesktopDirectoryHandle) || !desktopApp()?.folder.move) return { moved: [], failed: 0 };
        const top = accountDir(userId);
        const remembered = rememberedPlaces(userId);
        // A folder another account on this device put a workspace in is not certainly this one's:
        // it stays where it is, where both still find their files
        const shared = otherAccounts(userId).places;
        // Which of the folders a workspace's may be in are there
        const candidates = new Map<string, string[]>();
        for (const place of places) {
            const before = remembered[place.id];
            if (before) candidates.set(before, before.split('/'));
            if (top.length > 0) candidates.set(placeKey(place.dir), place.dir);
            candidates.set(sanitizeName(place.name), [sanitizeName(place.name)]);
        }
        const present = new Set<string>();
        const left = new Set<string>();
        for (const [key, dir] of candidates) {
            try {
                await openDir(root as unknown as FileSystemDirectoryHandle, dir);
                if (shared.has(key)) left.add(key); else present.add(key);
            } catch { /* not there */ }
        }
        const moves = planFolderMoves(places, remembered, dir => present.has(placeKey(dir)), unplacedNames, top);
        const moved: FolderMove[] = [];
        const failedIds = new Set<string>();
        for (const move of moves) {
            try {
                await moveWorkspaceFolder(root, move.from, move.to);
                moved.push(move);
                console.log(`📁 Moved ${placeKey(move.from)} to ${placeKey(move.to)}`);
            } catch (e) {
                failedIds.add(move.id);
                console.warn(`Could not move ${placeKey(move.from)} to ${placeKey(move.to)}:`, e);
            }
        }
        // Where each workspace's folder is now (a failed one is looked for where it was, and tried
        // again; one left to another account too, so that it is still found there)
        const next = { ...remembered };
        for (const place of places) {
            if (failedIds.has(place.id)) continue;
            const before = remembered[place.id];
            if (before && left.has(before)) continue;
            next[place.id] = placeKey([...top, ...place.dir]);
        }
        rememberPlaces(userId, next);
        // A move that failed may still have moved some files: views look again either way
        if (moves.length > 0) announceFolderChange(true);
        return { moved, failed: failedIds.size };
    });
    organising = run.catch(() => undefined);
    return run;
};

/**
 * A workspace's folder inside the local auto-save folder, for browsing it: the first of its
 * folders that is there (its place, else where it was before), as the chain of folders to it.
 */
export const openLocalWorkspaceFolder = async (root: FileSystemDirectoryHandle, workspaceName: string, userId?: string): Promise<FileSystemDirectoryHandle[] | null> => {
    for (const dir of workspaceDirs(workspaceName, userId)) {
        const chain: FileSystemDirectoryHandle[] = [root];
        try {
            for (const name of dir) chain.push(await chain[chain.length - 1].getDirectoryHandle(name));
            return chain;
        } catch { /* not there */ }
    }
    return null;
};

/**
 * The account's own folder inside the local auto-save folder, for browsing it, as the chain of
 * folders to it; null when the account has none yet, or none of its own.
 */
export const openLocalAccountFolder = async (root: FileSystemDirectoryHandle, userId?: string): Promise<FileSystemDirectoryHandle[] | null> => {
    const top = accountDir(userId);
    if (top.length === 0) return null;
    const chain: FileSystemDirectoryHandle[] = [root];
    try {
        for (const name of top) chain.push(await chain[chain.length - 1].getDirectoryHandle(name));
        return chain;
    } catch {
        return null;
    }
};

/** The local folder for browsing, and whether it can be read now. Unlike reading media, browsing does not need auto-save on. */
export type LocalFolderBrowse =
    | { state: 'unsupported' | 'none' }
    | { state: 'granted' | 'prompt' | 'denied'; root: FileSystemDirectoryHandle };

export const getLocalFolderForBrowsing = async (userId?: string): Promise<LocalFolderBrowse> => {
    if (!isFileSystemAccessSupported()) return { state: 'unsupported' };
    const root = await getStoredDirectoryHandle(userId);
    if (!root) return { state: 'none' };
    try {
        const state = await (root as any).queryPermission({ mode: 'read' });
        if (state === 'granted' || state === 'denied') return { state, root };
    } catch (e) {
        console.warn('queryPermission failed:', e);
    }
    return { state: 'prompt', root };
};

/** What came of asking for the local folder: allowed, refused (or no folder), or allowed but the folder is gone from disk. */
export type LocalFolderRequestResult = 'granted' | 'refused' | 'missing';

/**
 * Ask the browser to allow the local folder again. Call it straight from a click: the
 * browser only shows its prompt during a user action. Access is asked for reading and
 * writing, so auto-save works again too.
 */
export const requestLocalFolderAccess = async (userId?: string): Promise<LocalFolderRequestResult> => {
    const handle = await getStoredDirectoryHandle(userId);
    if (!handle) return 'refused';
    let granted = false;
    try {
        granted = (await (handle as any).requestPermission({ mode: 'readwrite' })) === 'granted';
    } catch (e) {
        console.warn('requestPermission failed:', e);
    }
    if (!granted) return 'refused';
    try {
        // The folder may have been moved or deleted since it was chosen
        await (handle as any).values().next();
    } catch (e) {
        if ((e as { name?: string })?.name === 'NotFoundError') return 'missing';
    }
    announceFolderChange(true);
    return 'granted';
};

/**
 * Extract category and filename from a storage path or file reference
 * e.g., "users/xxx/Workspace/Generated_Images/generated_images_123.png" -> { category: "Generated_Images", filename: "generated_images_123.png" }
 */
export const parseStoragePath = (storagePath: string): { category: string; filename: string } | null => {
    // Pattern: users/{userId}/{workspace}/{category}/{filename}
    const parts = storagePath.split('/');
    if (parts.length >= 2) {
        const filename = parts[parts.length - 1];
        const category = parts[parts.length - 2];
        return { category, filename };
    }
    return null;
};

export default {
    isFileSystemAccessSupported,
    pickSaveDirectory,
    saveFileLocally,
    autoSaveFile,
    getLocalSaveSettings,
    setAutoSaveEnabled,
    isAutoSaveReady,
    clearStoredDirectoryHandle,
    getStoredDirectoryHandle,
    verifyPermission,
    listLocalSaveFiles,
    downloadLocalFile,
    downloadAllFilesAsZip,
    getLocalSaveStats,
    syncCloudToLocal,
    saveWorkspaceLocally,
    isWorkspaceAutoSaveEnabled,
    setWorkspaceAutoSaveEnabled,
    renameWorkspaceFolder,
    loadWorkspaceLocally,
    readLocalFile,
    locateLocalFile,
    resolveFileLocally,
    storageWorkspaceOf,
    getLocalFolderAccess,
    requestLocalFolderAccess,
    setLocalFolderAccount,
    parseStoragePath,
    listLocalWorkflows,
    getWorkflowsDirectory,
    registerLocalFileMapping,
    getLocalFileMap,
    clearLocalFileMap,
    setLocalFileMap,
};

